/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';

import { useEffect, useRef, useState, createRef } from 'react';
import Script from 'next/script';
import { useSelector } from 'react-redux';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Grid } from '@/components/Grid';
import { defaultStoreUS, countryCodes } from '@/constants/locale';
import usePrevious from '@/utils/usePrevious';
import type { ReduxState } from '@/types';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { isBrandShelfPage } from '@/utils/isBrandShelfPage';
import { useProductList } from '../../../../context/useProductList';
import { YMMEModal } from '@/features/ymme/components/YMMEModal';
import style from '@/components/PageTemplates/Generic/styles.module.scss';
import { NoPartFound } from './NoPartFound';
import { SearchNoResults } from '@/features/search/components/SearchComponent/SearchNoResults';
import styles from './viewStyles.module.scss';
import { ShelfProduct } from './Product';
import type { Locale } from '@/types/i18n';
import { useLabel, useLabels } from '@/hooks/useLabels';
import environmentConfig from '@/config/environment';
import { FeatureFlag, useFeatureFlag } from '@/features/globalConfig';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import type { ShelfPart } from '@/types/legacy';
import { useRedirectAfterVehicleChanged } from '@/features/category/hooks/useRedirectAfterVehicleChanged';
import useRefDimensions from '@/hooks/useRefDimensions';
import { useProductListView } from '../../../../context/useProductListView';
import { useIsMobileQRExperience } from '@/hooks/useIsMobileQRExperience';
import { WarrantyModal } from '@/components/ProductDetail/ProductWarranty';
import { CMSCitrusShelf } from '@/features/contentstack/components/CMSCitrus/CMSCitrusShelf';
import { useSeoMetadata } from '@/features/common/api/getSeoMetadata';
import {
  useCitrusShelfData,
  useCitrusSearchPageData,
} from '@/features/citrus/api/getCitrusShelfData';
import { usePageType as usePageTypeLegacy } from '@/hooks/usePageType';
import { usePageType } from '@/features/category/api/getPageType';
import { LoadMoreResults } from '@/components/AZCustomComponent/LoadMoreResults';
import { LoadingIndicator } from '@/components/AZCustomComponent/LoadingIndicator';
import { useCombinedInfiniteQuery } from '@/hooks/useCombinedInfiniteQuery';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { sessionStorage } from '@/utils/sessionStorage';
import { useGetShelfPageRecords } from '@/features/shelf/hooks/useGetShelfPageRecords';
import { pageTypes } from '@/constants/page';
import { useProductShelfResults } from '@/features/category/api/getProductShelfResults';
import { useProductShelfACESResults } from '@/features/category/api/getProductShelfACESResults';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { LocalCartProvider } from '@/features/cart/context/CartContext';
import { useMerchShelfResults } from '@/features/category/api/getMerchShelfResults';
import { usePreviouslyBoughtItem } from '@/hooks/usePreviouslyBoughtItem';
import { stringifyUrl } from '@/utils/urlHelpers';
import { useFilterAndRedirect } from '@/hooks/useFilterAndRedirect';
import { Button } from '@/components/Button';
import { Label } from '@/features/i18n';

export type Props = {
  locale: Locale | undefined;
  handleDealClick?: (
    part: ShelfPart,
    price: string,
    quantity: number,
    ref: React.RefObject<HTMLButtonElement | null>,
    fulfillmentTypeId: number | null,
  ) => void;
  xFusionQueryId?: string;
  resultsListRef?: React.RefObject<HTMLDivElement | null>;
};

type WarrantyModal = {
  isShown: boolean;
  showModalFrom?: string;
  warrantyType?: string;
};

export const ResultsList = ({ locale, handleDealClick, xFusionQueryId, resultsListRef }: Props) => {
  const { pageType } = usePageTypeLegacy();
  const isShelfPage = pageType === pageTypes.ProductShelf || pageType === pageTypes.shelf;
  const matchesTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));
  const matchesMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const { acesEnabled } = useAcesFlags();
  const { query } = legacyRouter;

  const labelMap = {
    lblMakeModel: 'label_MakeModel',
    lblTryExpandingResults: 'label_try_expanding_results',
    lblExpandResults: 'label_expand_results',
  };
  const labels = useLabels(labelMap);

  const isUS = locale === countryCodes.us;
  const isMX = locale === countryCodes.mx;
  const shouldUseAces = acesEnabled && (isUS || isMX);

  const { listViewState } = useProductListView();
  const isListView = isBrandShelfPage(legacyRouter.asPath)
    ? listViewState.isBrandListView
    : listViewState.isListView;
  const [warrantyModal, setWarrantyModal] = useState<WarrantyModal>({
    isShown: false,
  });
  const shelfReadyStatus = useSelector(({ shelf }: ReduxState) => shelf.readyStatus);
  const redirectAfterVehicleChanged = useRedirectAfterVehicleChanged();

  const { isFetching: merchShelfResultsIsFetching } = useMerchShelfResults();

  const useShelfResultsFunction = acesEnabled ? useProductShelfACESResults : useProductShelfResults;
  const { data: productShelfResultsData, isFetching: productShelfResultsIsFetching } =
    useShelfResultsFunction();
  const {
    data: shelfData,
    loadMoreClicked,
    status,
    isFetching,
    additionalProductSkuDetailsIsFetching,
  } = useProductList();
  const {
    infiniteQueryStatus,
    hasNextPage,
    fetchNextPage,
    infiniteQueryIsPending,
    totalRecords,
    currentlyShowing,
    isFetchingNextPage: isLoadingMore,
  } = useCombinedInfiniteQuery();
  const loadingStatus =
    status === 'pending' ||
    additionalProductSkuDetailsIsFetching ||
    isFetching ||
    infiniteQueryStatus === 'pending' ||
    infiniteQueryIsPending;
  const parts = shelfData?.shelfParts;
  const shelfBody = shelfData?.shelfPageBody;
  const shelfBodyRecords = useGetShelfPageRecords(shelfBody);
  const { data: headerData } = useHeaderData();
  const miniCartMap = headerData?.miniCartMap;
  const miniCartItemCount = miniCartMap?.itemCount || 0;
  const previousMiniCartItemCount = usePrevious(miniCartItemCount);
  const isSearchPage = legacyRouter.pathname.includes('/searchresult');
  const isMerchShelfPage = legacyRouter.route === '/deals/[cmsPath]/[[...slug]]';
  const isSYMMPage = shelfData?.makeModelYearPageType;
  const { data: storeDetails } = useStoreDetailsData();

  const isProductResultsFetching =
    isMerchShelfPage && acesEnabled ? merchShelfResultsIsFetching : productShelfResultsIsFetching;

  const selectedFacetValue =
    productShelfResultsData?.productShelfResults &&
    'selectedFacetValueDisplayName' in productShelfResultsData.productShelfResults
      ? productShelfResultsData.productShelfResults.selectedFacetValueDisplayName
      : undefined;

  const { data: pageTypeData } = usePageType();
  const { data: seoMetadataInfo } = useSeoMetadata({
    enabled: !isProductResultsFetching,
    vehicleMake: pageTypeData?.make ?? undefined,
    vehicleModel: pageTypeData?.model ?? undefined,
    vehicleYear: pageTypeData?.year ?? undefined,
    selectedFacetValue,
  });
  const preferredVehicle = usePreferredVehicle();
  const prevVehicleId = usePrevious(
    acesEnabled ? preferredVehicle?.vehicleId : preferredVehicle?.catalogVehicleId,
  );
  const prevStoreNumber = sessionStorage.getItem('previousStore');
  const storeNumber =
    locale === countryCodes.us
      ? storeDetails?.storeNumber || defaultStoreUS
      : storeDetails?.storeNumber;

  const { vehicleId, modelId } = headerData?.vehicleMap || {};
  const hasVehicle = Boolean(vehicleId || modelId);
  const vehicleName = headerData?.vehicleMap?.vehicleName ?? '';

  const handleFilterAndRedirect = useFilterAndRedirect();

  // staticNavigation can be undefined, hence the need for optional chaining
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const fitmentFacet = shelfData?.shelfLeftNav?.[0]?.staticNavigation?.find(
    (f) => f.dimensionName === 'universal_or_specific_fit_s',
  );
  const fitmentRefinements = fitmentFacet?.refinements ?? [];
  const vehicleSpecificEntry = fitmentRefinements.find(
    (r) => r.properties.internalName === 'VEHICLE_SPECIFIC',
  );
  const universalEntry = fitmentRefinements.find((r) => r.properties.internalName === 'UNIVERSAL');
  const vehicleSpecificSelected = vehicleSpecificEntry?.properties.isSelected === true;
  const universalSelected = universalEntry?.properties.isSelected === true;
  const showExpandResults =
    vehicleSpecificEntry != null &&
    universalEntry != null &&
    vehicleSpecificSelected &&
    !universalSelected;
  const activeRefinement = vehicleSpecificSelected ? vehicleSpecificEntry : universalEntry;

  const handleExpandResults = () => {
    if (!fitmentFacet || !activeRefinement) {
      return;
    }
    sessionStorage.setItem('expandResultsAnalytics', 'true');
    handleFilterAndRedirect({
      refinement: activeRefinement,
      dimensionName: fitmentFacet.dimensionName,
      displayName: fitmentFacet.displayName,
      checked: false,
      isStandardFilter: true,
    });
  };

  const { setPageSpecificData } = usePageSpecificData();
  const shelfCatLabel =
    typeof legacyRouter.query.searchText === 'string'
      ? legacyRouter.query.searchText
      : (seoMetadataInfo?.pageHeadingName ?? shelfData?.shelfCatLabel);

  const brandNameShelf = shelfData?.brandName;

  let totalNumRecords = totalRecords ?? 0;
  if (preferredVehicle && isShelfPage && parts) {
    totalNumRecords += parts.filter(
      (product) =>
        !shelfBodyRecords?.records?.some((record) => record.skuNumber === product.skuNumber),
    ).length;
  } else {
    // Only add sponsored products count if they come from Citrus
    const backendRecordsCount = shelfData?.shelfParts?.length ?? 0;
    const backendTotalCount = totalRecords ?? 0;

    // If backend total count doesn't match records count, the difference likely comes from Citrus
    const hasCitrusProducts = backendTotalCount !== backendRecordsCount;

    if (hasCitrusProducts) {
      const sponsoredProductsCount =
        parts?.filter((product) => product.isSponsoredCard).length ?? 0;
      totalNumRecords += sponsoredProductsCount;
    }
  }

  const handleCloseWarrantyModal = () => setWarrantyModal({ isShown: false });

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );

  const previousWindowWidth = usePrevious(windowWidth);
  const shelfGridDimensions = useRefDimensions(resultsListRef);
  const [resultListHeight, setResultListHeight] = useState(shelfGridDimensions.height);
  const previousResultListHeight = usePrevious(resultListHeight);
  const [showYmme, setShowYMME] = useState<boolean>(false);
  const [ymmePartGroupID, setPartGroupIDForYMME] = useState<string>();
  const [ymmePartGroupName, setPartGroupNameForYmme] = useState<string>();
  const [resizedGridContents, setResizedGridContents] = useState(false);
  const shelfItemRefs = useRef<Array<React.RefObject<HTMLDivElement>>>([]);
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };
  const setElementHeight = (el: HTMLElement | null | undefined, newHeight?: number) => {
    if (newHeight && el) {
      el.style.minHeight = `${newHeight}px`;
    } else if (el) {
      el.style.minHeight = 'auto';
    }
  };
  const [significantChange, setSignificantChange] = useState(false);

  useEffect(() => {
    if (significantChange && legacyRouter.query.loadMore && !isLoadingMore) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { loadMore, ...restQuery } = legacyRouter.query;
      if (legacyRouter.push) {
        void legacyRouter.push(
          {
            pathname: legacyRouter.pathname,
            query: restQuery,
          },
          undefined,
          { shallow: true },
        );
      } else {
        router.push(
          stringifyUrl({
            url: legacyRouter.pathname,
            query: restQuery,
          }),
        );
      }
      setSignificantChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    significantChange,
    legacyRouter.query,
    legacyRouter.push,
    legacyRouter.pathname,
    isLoadingMore,
    router,
  ]);

  // When vehicle changes
  useEffect(() => {
    const vehicleId = acesEnabled
      ? preferredVehicle?.vehicleId
      : preferredVehicle?.catalogVehicleId;

    if (
      (prevVehicleId !== vehicleId && !!prevVehicleId) ||
      (prevVehicleId === undefined && !!vehicleId)
    ) {
      setSignificantChange(true);
    }
  }, [acesEnabled, preferredVehicle?.catalogVehicleId, preferredVehicle?.vehicleId, prevVehicleId]);

  // When store changes
  useEffect(() => {
    if (
      legacyRouter.query.loadMore &&
      !!prevStoreNumber &&
      storeDetails?.storeNumber !== prevStoreNumber &&
      storeDetails?.storeNumber
    ) {
      setSignificantChange(true);
      scrollToTop();
      sessionStorage.removeItem('previousStore');
    }
  }, [storeDetails?.storeNumber, prevStoreNumber, legacyRouter]);

  const getSectionElement = (
    column: HTMLElement | null,
    section: string,
  ): HTMLElement | undefined => {
    const sectionElements = column ? Array.from(column.querySelectorAll<HTMLElement>(section)) : [];
    if (sectionElements.length > 1) {
      if (section === '.fitment-button-container') {
        return sectionElements.find((elm) => elm.offsetParent !== null);
      }
      return sectionElements.find((elm) => elm.offsetHeight > 0);
    }

    return sectionElements[0];
  };

  function getSectionHeightForColumn(column: HTMLElement | null, section: string) {
    if (column) {
      const sectionElement = getSectionElement(column, section);
      if (sectionElement) {
        setElementHeight(sectionElement);
      }

      return sectionElement?.offsetHeight;
    }
  }

  const reSizableSectionClasses = [
    '.fulfillment-option-wrapper',
    '.product-title',
    '.product-price-wrapper',
    '.product-detail-wrapper',
    '.fitment-button-container',
    '.product-notes-grid-view',
    '.product-attributes-container',
    '.purchased-date',
  ];
  // TODO replace with useMediaQuery
  const isWidthMobileOrTablet = windowWidth <= 1139 || window.outerWidth <= 1139;
  const isTabletOnly = windowWidth <= 1139 && windowWidth > 719;
  const RR_AZRMOB_SHELF_ENABLED = useFeatureFlag('RR_AZRMOB_SHELF_ENABLED');
  const RR_AZRWEB_SHELF_ENABLED = useFeatureFlag('RR_AZRWEB_SHELF_ENABLED');
  const customReviewsEnabled = useFeatureFlag('CUSTOM_REVIEWS_ENABLED') === 'true';
  const { BV_MOBILE_TAG, BV_DESKTOP_TAG } = environmentConfig;

  const customHeaderScript =
    customReviewsEnabled || !isUS
      ? null
      : isWidthMobileOrTablet && RR_AZRMOB_SHELF_ENABLED === 'true'
        ? BV_MOBILE_TAG
        : RR_AZRWEB_SHELF_ENABLED === 'true'
          ? BV_DESKTOP_TAG
          : '';

  const citrusShelfBannersEnabled = useFeatureFlag('IS_CITRUS_SHELF_BANNERS_ENABLED') === 'true';
  const citrusSearchBannersEnabled =
    useFeatureFlag('IS_CITRUS_SEARCH_RESULTS_BANNERS_ENABLED') === 'true';

  const { data: citrusShelfData } = useCitrusShelfData({
    enabled: citrusShelfBannersEnabled,
  });
  const { data: citrusSearchData } = useCitrusSearchPageData({
    enabled: citrusSearchBannersEnabled,
  });

  const citrusData = isSearchPage ? citrusSearchData : citrusShelfData;
  const citrusBannerSlotId = isSearchPage
    ? 'in-grid-midpage-banner'
    : 'category-shelf-midpage-banner';
  const hasCitrusMidBanner =
    (citrusShelfBannersEnabled || citrusSearchBannersEnabled) &&
    !!citrusData?.ads?.find((ads) => {
      const slotId = ads.slotId;
      const banners = ads.banners;
      return slotId === citrusBannerSlotId && !!banners?.length;
    });

  function resizeGridContents(shelfItems: Array<React.RefObject<HTMLElement>>) {
    const shelfItemsLength = shelfItems.length;
    const columnsPerRow = matchesTablet ? 2 : 3;
    const numOfRows = Math.ceil(shelfItemsLength / columnsPerRow);
    let i;

    for (i = 0; i < numOfRows; i++) {
      const columnStartIndex = i * columnsPerRow;
      const columns = shelfItems.slice(columnStartIndex, columnStartIndex + columnsPerRow);
      reSizableSectionClasses.forEach((section) => {
        let longestColumnSectionHeight = 0;
        columns.forEach((column) => {
          const currentColumn = column.current;
          const columnSectionHeight = getSectionHeightForColumn(currentColumn, section);

          if (columnSectionHeight && columnSectionHeight > longestColumnSectionHeight) {
            longestColumnSectionHeight = columnSectionHeight;
          }
        });
        columns.forEach((column) => {
          const currentColumn = column.current;
          const columnSection = getSectionElement(currentColumn, section);
          setElementHeight(columnSection, longestColumnSectionHeight);
        });
      });
    }
  }

  function resetGridHeights(shelfItems: Array<React.RefObject<HTMLElement>>) {
    shelfItems.forEach((shelfItem) => {
      const currentItem = shelfItem.current;
      // shelfItem.current can be null at runtime when refs are not yet attached
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!currentItem) {
        return;
      }
      reSizableSectionClasses.forEach((section) => {
        const shelfItemSection = currentItem.querySelectorAll<HTMLElement>(section)[0];
        setElementHeight(shelfItemSection);
      });
    });
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      let timeoutId: ReturnType<typeof setTimeout>;
      resizeGrids();
      const resizeListener = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => setWindowWidth(window.innerWidth), 150);
      };

      window.addEventListener('resize', resizeListener);

      return () => {
        window.removeEventListener('resize', resizeListener);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resizeGrids = () => {
    const shelfItems = shelfItemRefs.current;
    const shelfItemsLength = shelfItems.length;
    const shelfItemsExist = shelfItemsLength > 1;
    if (shelfItemsExist && !isListView && !matchesMobile) {
      resizeGridContents(shelfItems);
      setResizedGridContents(true);
    } else if (resizedGridContents && (isListView || matchesMobile)) {
      resetGridHeights(shelfItems);
      setResizedGridContents(false);
    }
  };

  useEffect(() => {
    if (resultsListRef?.current) {
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver.observe(resultsListRef.current);
      }
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    if (
      (previousResultListHeight === 0 && resultListHeight === 0) ||
      previousResultListHeight !== resultListHeight ||
      windowWidth !== previousWindowWidth ||
      miniCartItemCount !== previousMiniCartItemCount
    ) {
      timeoutId = setTimeout(() => {
        resizeGrids();
      }, 150);
    }

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListView, resultListHeight, windowWidth, miniCartItemCount]);

  if (parts && shelfItemRefs.current.length !== parts.length) {
    shelfItemRefs.current = Array(parts.length)
      .fill(null)
      .map((_, i) => shelfItemRefs.current[i] || createRef());
  }

  const toggleShowYMMECallback = (partGroupID?: string, partGroupName?: string) => {
    setPartGroupIDForYMME(showYmme ? undefined : partGroupID);
    setPartGroupNameForYmme(showYmme ? undefined : partGroupName);
    setShowYMME(!showYmme);
  };

  const hasSearchTextQuery = Boolean(query.searchText);
  const isMobileQRExperience = useIsMobileQRExperience();

  const shouldShowSecondBanner = (!isSYMMPage || hasSearchTextQuery) && parts && parts.length > 3;

  const whereToShowBanner = !isListView && isTabletOnly ? 4 : 3;
  const brandPartsNotAvailable = useLabel(
    'label_brandpage_resultsList_isCurrentlyNotAvailableForYour',
  );
  const forLabel = useLabel('label_Search_for');
  const forYourLabel = useLabel('label_Search_for_your');
  const resultsLabel = useLabel('label_Search_results');
  const noPartsFoundLabel = isBrandShelfPage(legacyRouter.asPath)
    ? `${brandNameShelf ?? ''} ${brandPartsNotAvailable}`
    : `${parts?.length ? String(parts.length) : '0'} ${resultsLabel} ${forLabel} ${
        shelfCatLabel ?? ''
      } ${forYourLabel}`;

  let resizeObserver: ResizeObserver | undefined;
  const showLoadmoreOrBackToTopButton =
    parts?.length && !loadingStatus && status === 'success' && !isLoadingMore;

  try {
    resizeObserver = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        entries.forEach((entry) => {
          if (entry.target.getBoundingClientRect().height !== previousResultListHeight) {
            setResultListHeight(entry.target.getBoundingClientRect().height);
          }
        });
      });
    });
  } catch {
    resizeObserver = undefined;
  }

  const getPreviouslyBoughtItem = usePreviouslyBoughtItem();

  return (
    <LocalCartProvider>
      <Grid
        justify="center"
        alignItems="center"
        container
        id="shelf-results-container"
        data-testid="shelf-results-container-test"
      >
        <Grid
          container
          spacing={0}
          ref={resultsListRef}
          className={style.results}
          id="shelf-result-list"
        >
          {parts?.length === 0 ? (
            isSearchPage || isShelfPage ? (
              <SearchNoResults
                queryDisplayLabel={shelfCatLabel ?? undefined}
                vehicleName={vehicleName || undefined}
                showExpandResults={
                  vehicleSpecificEntry != null &&
                  universalEntry != null &&
                  vehicleSpecificSelected &&
                  !universalSelected
                }
              />
            ) : (
              <NoPartFound noPartsFoundLabel={noPartsFoundLabel} vehicleName={vehicleName} />
            )
          ) : null}
          <ul>
            {parts?.slice(0, whereToShowBanner).map((part, index) => (
              <ShelfProduct
                key={`${part.skuNumber}${index}`}
                hasVehicle={hasVehicle}
                pageType={pageType}
                index={index}
                onDealClick={handleDealClick}
                part={part}
                shelfItemRef={shelfItemRefs.current[index]}
                isListItem
                shelfReadyStatus={shelfReadyStatus}
                storeNumber={storeNumber}
                toggleShowYmme={toggleShowYMMECallback}
                setWarrantyModal={setWarrantyModal}
                resizeShelfGrids={
                  !resizeObserver
                    ? () =>
                        setTimeout(() => {
                          resizeGrids();
                        }, 150)
                    : undefined
                }
                xFusionQueryId={xFusionQueryId}
                previouslyBoughtItem={getPreviouslyBoughtItem(String(part.skuNumber))}
              />
            ))}
            {shouldShowSecondBanner && !isMobileQRExperience ? (
              <>
                <FeatureFlag flag="IS_CITRUS_ENABLED">
                  {hasCitrusMidBanner ? (
                    <li className={styles.secondBannerBackGroundCard}>
                      <CMSCitrusShelf content={{ slot_id: citrusBannerSlotId }} />
                    </li>
                  ) : null}
                </FeatureFlag>
              </>
            ) : null}
            {parts?.slice(whereToShowBanner).map((part, index) => {
              return (
                <ShelfProduct
                  key={`${part.skuNumber}_${index}`}
                  pageType={pageType}
                  hasVehicle={hasVehicle}
                  index={index + whereToShowBanner}
                  onDealClick={handleDealClick}
                  part={part}
                  isListItem
                  shelfItemRef={shelfItemRefs.current[index + whereToShowBanner]}
                  shelfReadyStatus={shelfReadyStatus}
                  storeNumber={storeNumber}
                  toggleShowYmme={toggleShowYMMECallback}
                  setWarrantyModal={setWarrantyModal}
                  resizeShelfGrids={
                    !resizeObserver
                      ? () =>
                          setTimeout(() => {
                            resizeGrids();
                          }, 150)
                      : undefined
                  }
                  xFusionQueryId={xFusionQueryId}
                  previouslyBoughtItem={getPreviouslyBoughtItem(String(part.skuNumber))}
                />
              );
            })}
          </ul>

          <YMMEModal
            onUpdateVehicleSuccess={redirectAfterVehicleChanged}
            showYmme={showYmme}
            partGroupId={ymmePartGroupID}
            partGroupName={ymmePartGroupName}
            closeYmmeWidget={() => toggleShowYMMECallback(ymmePartGroupID, ymmePartGroupName)}
            selectedMethod={isSYMMPage && shouldUseAces ? labels.lblMakeModel : undefined}
            preselectedMakeId={
              shouldUseAces
                ? (productShelfResultsData?.productShelfResults?.makeId as number)
                : undefined
            }
          />
          {warrantyModal.isShown ? (
            <WarrantyModal
              showWarranty={warrantyModal.isShown}
              closeWarrantyModal={handleCloseWarrantyModal}
              warrantyType={warrantyModal.warrantyType}
            />
          ) : null}
          {customHeaderScript ? <Script src={customHeaderScript} strategy="lazyOnload" /> : null}
        </Grid>
        {(isLoadingMore || loadingStatus) && !!parts?.length && loadMoreClicked && (
          <LoadingIndicator />
        )}
        {!!parts?.length && showLoadmoreOrBackToTopButton && (
          <LoadMoreResults
            totalRecords={totalNumRecords}
            hasNextPage={hasNextPage}
            currentlyShowing={currentlyShowing}
            fetchMore={async () => {
              if (!legacyRouter.query.loadMore) {
                if (legacyRouter.push) {
                  await legacyRouter.push(
                    {
                      pathname: legacyRouter.pathname,
                      query: { ...legacyRouter.query, loadMore: 'true' },
                    },
                    legacyRouter.asPath,
                    { shallow: true },
                  );
                } else {
                  router.push(
                    stringifyUrl({
                      url: legacyRouter.pathname,
                      query: { ...legacyRouter.query, loadMore: 'true' },
                    }),
                  );
                }
              }
              if (fetchNextPage) {
                await fetchNextPage();
                setPageSpecificData({ productFindingMethod: 'loadMore' });
              }
            }}
          />
        )}
        {!!parts?.length && showExpandResults && (
          <div className={styles.expandResultsFooter}>
            <p className={styles.expandResultsFooterText}>
              <Label label={labelMap.lblTryExpandingResults} />
            </p>
            <div className={styles.expandResultsFooterButton}>
              <Button fullWidth variant="contained" onClick={handleExpandResults}>
                <Label label={labelMap.lblExpandResults} />
              </Button>
            </div>
          </div>
        )}
      </Grid>
    </LocalCartProvider>
  );
};
